import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BaseStoreEntityType, baseStoreEntityValue } from 'src/store/types';
import { SafeAnyType } from 'src/utils/safeAny';
import {
  AccountOrganizerAuthorizationOrder,
  AccountOrganizerChangeAdditionalListInfoType,
  AccountOrganizerChangeDataInListType,
  AccountOrganizerClientSecret,
  AccountOrganizerContributionStartType,
  AccountOrganizerEnum,
  AccountOrganizerGetListErrorType,
  AccountOrganizerGetListOkType,
  AccountOrganizerGetListStartType,
  AccountOrganizerPayPalContributionStartType,
  AccountOrganizerRecurringContributionStartType,
  AccountOrganizerRecurringPayPalContributionStartType,
  AccountOrganizerSaveContributionOkType,
  AccountOrganizerSaveContributionStartType,
} from 'src/models/Account/Organizer';
import { CommonApiErrorActionType } from 'src/models/BaseApi';
import { AccountOrganizerExternalContributionLoadResponseType } from 'src/models/Account/Organizer/externalContributions';
import { CommonTableResponseType } from 'src/models/Table';
import { PaymentRecoveryCompleteResponseType } from 'src/models/Account/Organizer/paymentRecovery';

export type StateWithStripeClientSecretType = {
  clientSecret: null | string;
  value: BaseStoreEntityType<SafeAnyType>;
};

export type StateWithPayPalAuthorizationOrderType = {
  authorizationOrderId: null | string;
  value: BaseStoreEntityType<SafeAnyType>;
};

export type StateWithPayPalCaptureOrderType = {
  payPalOrderId: null | string;
  value: BaseStoreEntityType<SafeAnyType>;
};

export type AccountOrganizerStateType = {
  typedEntity: Record<string, BaseStoreEntityType<SafeAnyType>>;
  saveContribution: BaseStoreEntityType<AccountOrganizerExternalContributionLoadResponseType>;
  saveRecurringContribution: StateWithStripeClientSecretType;
  savePayPalRecurringContribution: StateWithPayPalAuthorizationOrderType;
  paymentRecovery: StateWithStripeClientSecretType;
  paymentPayPalRecovery: StateWithPayPalCaptureOrderType;
};

const setPartialStore =
  (state: AccountOrganizerStateType, type: AccountOrganizerEnum | 'saveContribution') =>
  (values: BaseStoreEntityType<SafeAnyType>): void => {
    // typedEntity is not only available here
    // what about saveContribution/saveRecurringContribution/paymentRecovery?
    const mutableStorePart = (state as SafeAnyType)[type] || state.typedEntity[type];

    mutableStorePart.loading = values.loading;
    mutableStorePart.response = values.response;
    mutableStorePart.error = values.error;
  };

const initialState: AccountOrganizerStateType = {
  typedEntity: Object.values(AccountOrganizerEnum).reduce(
    (acc, name) => ({ ...acc, [name]: baseStoreEntityValue() }),
    {}
  ),
  saveContribution: baseStoreEntityValue(),
  saveRecurringContribution: {
    clientSecret: null,
    value: baseStoreEntityValue(),
  },
  savePayPalRecurringContribution: {
    authorizationOrderId: null,
    value: baseStoreEntityValue(),
  },
  paymentRecovery: {
    clientSecret: null,
    value: baseStoreEntityValue(),
  },
  paymentPayPalRecovery: {
    payPalOrderId: null,
    value: baseStoreEntityValue(),
  },
};

export const accountOrganizerSlice = createSlice({
  name: 'accountOrganizer',
  initialState,
  reducers: {
    getListStart: (state, { payload: { type } }: PayloadAction<AccountOrganizerGetListStartType>) => {
      setPartialStore(state, type)(baseStoreEntityValue(null, true));
    },
    getListOk: (state, { payload: { type, data } }: PayloadAction<AccountOrganizerGetListOkType>) => {
      setPartialStore(state, type)(baseStoreEntityValue(data, false));
    },
    clearList: (state, { payload: type }: PayloadAction<AccountOrganizerEnum>) => {
      setPartialStore(state, type)(baseStoreEntityValue(null, false));
    },
    getListError: (state, { payload: { type, error } }: PayloadAction<AccountOrganizerGetListErrorType>) => {
      setPartialStore(state, type)(baseStoreEntityValue(null, false, error));
    },
    changeAdditionalListInfo: (state, { payload }: PayloadAction<AccountOrganizerChangeAdditionalListInfoType>) => {
      state.typedEntity[payload.type].response = { ...state.typedEntity[payload.type].response, ...payload.data };
    },
    changeInListInfo: (state, { payload }: PayloadAction<AccountOrganizerChangeDataInListType>) => {
      const items = state.typedEntity[payload.type].response?.Items as
        | CommonTableResponseType<SafeAnyType>['Items']
        | undefined;

      if (items && items.length) {
        const foundIndex = items.findIndex((item) => item.ID === payload.id);

        if (~foundIndex) {
          items[foundIndex] = {
            ...items[foundIndex],
            ...payload.data,
          };
        }
      }
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    saveContributionStart: (state, action: PayloadAction<AccountOrganizerSaveContributionStartType>) => {
      setPartialStore(state, 'saveContribution')(baseStoreEntityValue(null, true));
    },
    saveContributionOk: (state, { payload: { data } }: PayloadAction<AccountOrganizerSaveContributionOkType>) => {
      setPartialStore(state, 'saveContribution')(baseStoreEntityValue(data, false));
    },
    saveContributionError: (state, { payload }: CommonApiErrorActionType) => {
      setPartialStore(state, 'saveContribution')(baseStoreEntityValue(null, false, payload));
    },
    saveContributionReset: (state) => {
      setPartialStore(state, 'saveContribution')(baseStoreEntityValue(null, false));
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    saveRecurringContributionStart: (state, action: PayloadAction<AccountOrganizerRecurringContributionStartType>) => {
      state.saveRecurringContribution = {
        clientSecret: state.saveRecurringContribution.clientSecret,
        value: baseStoreEntityValue(null, true),
      };
    },
    saveRecurringContributionOk: (state, { payload }: PayloadAction<boolean>) => {
      state.saveRecurringContribution.value = baseStoreEntityValue(payload, false);
    },
    saveRecurringContributionClientSecret: (state, { payload }: PayloadAction<AccountOrganizerClientSecret>) => {
      state.saveRecurringContribution.clientSecret = payload.ClientSecret;
    },
    saveRecurringContributionError: (state, { payload }: CommonApiErrorActionType) => {
      state.saveRecurringContribution = {
        clientSecret: state.saveRecurringContribution.clientSecret,
        value: baseStoreEntityValue(null, false, payload),
      };
    },
    saveRecurringContributionReset: (state) => {
      state.saveRecurringContribution = {
        clientSecret: null,
        value: baseStoreEntityValue(),
      };
    },

    saveRecurringPayPalContributionStart: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      action: PayloadAction<AccountOrganizerRecurringPayPalContributionStartType>
    ) => {
      state.savePayPalRecurringContribution = {
        authorizationOrderId: state.savePayPalRecurringContribution.authorizationOrderId,
        value: baseStoreEntityValue(null, true),
      };
    },
    saveRecurringPayPalContributionOk: (state, { payload }: PayloadAction<boolean>) => {
      state.savePayPalRecurringContribution.value = baseStoreEntityValue(payload, false);
    },
    saveRecurringPayPalContributionAuthorizationOrderId: (
      state,
      { payload }: PayloadAction<AccountOrganizerAuthorizationOrder>
    ) => {
      state.savePayPalRecurringContribution.authorizationOrderId = payload.AuthorizationOrderId;
    },
    saveRecurringPayPalContributionError: (state, { payload }: CommonApiErrorActionType) => {
      state.savePayPalRecurringContribution = {
        authorizationOrderId: state.savePayPalRecurringContribution.authorizationOrderId,
        value: baseStoreEntityValue(null, false, payload),
      };
    },
    saveRecurringPayPalContributionReset: (state) => {
      state.savePayPalRecurringContribution = {
        authorizationOrderId: null,
        value: baseStoreEntityValue(),
      };
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    paymentRecoveryStart: (state, action: PayloadAction<AccountOrganizerContributionStartType>) => {
      state.paymentRecovery.value = baseStoreEntityValue(null, true);
    },
    paymentRecoverySecret: (state, { payload }: PayloadAction<AccountOrganizerClientSecret>) => {
      state.paymentRecovery.clientSecret = payload.ClientSecret;
    },
    paymentRecoveryOK: (state, { payload }: PayloadAction<PaymentRecoveryCompleteResponseType>) => {
      state.paymentRecovery.value = baseStoreEntityValue(payload);
    },
    paymentRecoveryError: (state, { payload }: CommonApiErrorActionType) => {
      state.paymentRecovery.value = baseStoreEntityValue(null, false, payload);
    },
    paymentRecoveryReset: (state) => {
      state.paymentRecovery = {
        clientSecret: null,
        value: baseStoreEntityValue(),
      };
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    paymentRecoveryPayPalStart: (state, action: PayloadAction<AccountOrganizerPayPalContributionStartType>) => {
      state.paymentPayPalRecovery.value = baseStoreEntityValue(null, true);
    },
    paymentRecoveryPayPalSecret: (state, { payload }: PayloadAction<AccountOrganizerClientSecret>) => {
      state.paymentPayPalRecovery.payPalOrderId = payload.ClientSecret;
    },
    paymentRecoveryPayPalOK: (state, { payload }: PayloadAction<PaymentRecoveryCompleteResponseType>) => {
      state.paymentPayPalRecovery.value = baseStoreEntityValue(payload);
    },
    paymentRecoveryPayPalError: (state, { payload }: CommonApiErrorActionType) => {
      state.paymentPayPalRecovery.value = baseStoreEntityValue(null, false, payload);
    },
    paymentRecoveryPayPalReset: (state) => {
      state.paymentPayPalRecovery = {
        payPalOrderId: null,
        value: baseStoreEntityValue(),
      };
    },
  },
});
